import { ArrowDownToLine, ArrowUpFromLine, Cpu, Info, Link2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { fmtDateTime } from '../../../shared/format'
import type { CatalogModuleView } from '../../types'
import { usePriceFormat } from '../../usePriceFormat'

function Card({ icon: Icon, title, children }: { icon: typeof Info; title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border bg-card shadow-card">
      <h2 className="flex items-center gap-2 border-b px-4 py-3 font-semibold">
        <Icon className="size-4 text-muted-foreground" aria-hidden />
        {title}
      </h2>
      <div className="p-4">{children}</div>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  )
}

/** Fiyatlandırma, devreye alma bilgileri, teknik gereksinimler ve modül bağımlılıkları. */
export function ModuleInfoAside({ module: m, catalog }: { module: CatalogModuleView; catalog: CatalogModuleView[] }) {
  const { t } = useTranslation()
  const fmtPrice = usePriceFormat()
  const byId = new Map(catalog.map((x) => [x.id, x]))
  const requires = m.dependsOn.map((id) => byId.get(id)).filter((x): x is CatalogModuleView => !!x)
  const requiredBy = catalog.filter((x) => x.dependsOn.includes(m.id))
  const required = m.usage.filter((u) => u.requirement === 'required').length

  const moduleLinks = (list: CatalogModuleView[], empty: string) =>
    list.length ? (
      <ul className="space-y-1.5">
        {list.map((x) => (
          <li key={x.id}>
            <Link to={`/projects/catalog/${x.id}`} className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60">
              <span className="truncate font-medium">{x.name}</span>
              <span className="font-mono text-[11px] text-muted-foreground">{x.code}</span>
            </Link>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-xs text-muted-foreground">{empty}</p>
    )

  return (
    <aside className="space-y-4">
      <Card icon={Info} title={t('projects.catalog.detail.info')}>
        <div className="mb-3 rounded-lg bg-primary/[0.06] p-3">
          <div className="text-[11px] text-muted-foreground">{t('projects.catalog.fields.listPrice')}</div>
          <div className="text-2xl font-semibold tabular-nums">
            {fmtPrice(m.price)}
            {m.pricing !== 'one_time' ? <span className="text-sm font-normal text-muted-foreground"> {t(`projects.pricingSuffix.${m.pricing}`)}</span> : null}
          </div>
          <div className="text-xs text-muted-foreground">{t(`projects.pricing.${m.pricing}`)}</div>
        </div>
        <dl className="divide-y">
          <Row label={t('projects.catalog.fields.version')}>v{m.version}</Row>
          <Row label={t('projects.catalog.fields.setupHours')}>{t('projects.taskDetail.hours', { count: m.setupHours })}</Row>
          <Row label={t('projects.catalog.fields.trialDays')}>{m.trialDays ? t('common.days', { count: m.trialDays }) : t('projects.catalog.detail.noTrial')}</Row>
          <Row label={t('projects.catalog.detail.usage')}>{t('projects.catalog.detail.usageValue', { count: m.usage.length, required })}</Row>
          <Row label={t('projects.catalog.detail.featureCount')}>{m.features.length}</Row>
          <Row label={t('projects.fields.updatedAt')}>
            <span className="tabular-nums">{fmtDateTime(m.updatedAt)}</span>
          </Row>
        </dl>
      </Card>

      <Card icon={Cpu} title={t('projects.catalog.detail.requirements')}>
        {m.requirements.length ? (
          <ul className="list-disc space-y-1.5 pl-4 text-sm marker:text-muted-foreground">
            {m.requirements.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">{t('projects.catalog.detail.noRequirements')}</p>
        )}
      </Card>

      <Card icon={Link2} title={t('projects.catalog.detail.dependencies')}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ArrowDownToLine className="size-3.5" aria-hidden />
              {t('projects.modules.requires')}
            </p>
            {moduleLinks(requires, t('projects.catalog.detail.noRequires'))}
          </div>
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <ArrowUpFromLine className="size-3.5" aria-hidden />
              {t('projects.catalog.detail.requiredBy')}
            </p>
            {moduleLinks(requiredBy, t('projects.catalog.detail.noRequiredBy'))}
          </div>
        </div>
      </Card>
    </aside>
  )
}
